import type HttpClient from "../../tastyworks/http"
import { MARKET_DATA_DESER, MarketData } from "../../tastyworks/market-data"
import { ItemsResponse, parseItemsResponse } from "../../tastyworks/response"

// by-type rejects >100 instruments, and Tomcat 400s when the request line plus
// headers exceed maxHttpHeaderSize (8 KB). Event-contract symbols vary widely in
// length, so we bound batches by both a symbol count and a query-byte budget.
const MARKET_DATA_MAX_SYMBOLS = 100
const MARKET_DATA_MAX_QUERY_BYTES = 3000

export class MarketDataService {
  private readonly marketDataClient: HttpClient

  constructor(baseHttpClient: HttpClient) {
    this.marketDataClient = baseHttpClient.nested("market-data")
  }

  // Snapshot bid/ask for a set of event-contract streamer symbols. The endpoint
  // expects bare repeated query keys (event-contract=A&event-contract=B); the
  // shared Params serializer would dasherize and append "[]" to an array, so we
  // build the query string by hand. Symbols are opaque backend strings — pass
  // them through verbatim, only URL-encoding the value.
  readonly byEventContractType = async (
    symbols: string[]
  ): Promise<ItemsResponse<MarketData>> => {
    const batches = this.batchSymbols(symbols)
    const responses = await Promise.all(batches.map(this.fetchBatch))
    const merged = new ItemsResponse<MarketData>()
    for (const response of responses) {
      merged.items.push(...response.items)
    }
    // Error only if EVERY batch failed; a partial failure still returns the
    // batches that succeeded rather than discarding the whole snapshot.
    if (responses.length > 0 && responses.every((r) => r.isError)) {
      merged.errorContainer = responses[0].errorContainer
    }
    return merged
  }

  private readonly batchSymbols = (symbols: string[]): string[][] => {
    const batches: string[][] = []
    let current: string[] = []
    let currentBytes = 0
    for (const symbol of symbols) {
      const cost = `event-contract=${encodeURIComponent(symbol)}&`.length
      const wouldExceed =
        current.length >= MARKET_DATA_MAX_SYMBOLS ||
        currentBytes + cost > MARKET_DATA_MAX_QUERY_BYTES
      if (current.length > 0 && wouldExceed) {
        batches.push(current)
        current = []
        currentBytes = 0
      }
      current.push(symbol)
      currentBytes += cost
    }
    if (current.length > 0) {
      batches.push(current)
    }
    return batches
  }

  private readonly fetchBatch = async (
    symbols: string[]
  ): Promise<ItemsResponse<MarketData>> => {
    const query = symbols
      .map((symbol) => `event-contract=${encodeURIComponent(symbol)}`)
      .join("&")
    const helper = await this.marketDataClient.get(`by-type?${query}`)
    return parseItemsResponse(helper, MARKET_DATA_DESER.toParser(MarketData))
  }
}
